import React, { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LoginStatusIndicator from '@components/common/elements/LoginStatusIndicator';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DATE_FORMAT_VALUE, DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { deviceStatus, expireKitplayOptions } from '@constants/masterData';
import useListBase from '@hooks/useListBase';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { convertUtcToLocalTime } from '@utils';
import { setData } from '@utils/localStorage';
import { Tag } from 'antd';

import styles from '../index.module.scss';

const message = defineMessages({
    objectName: 'Device',
});

const DeviceListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const { customerId } = useParams();
    const statusValues = translate.formatKeys(deviceStatus, ['label']);
    const expireValues = translate.formatKeys(expireKitplayOptions, ['label']);
    const [fetchedAt, setFetchedAt] = useState(null);

    const { data, mixinFuncs, queryFilter, loading, pagination, queryParams, changePagination, serializeParams } =
        useListBase({
            apiConfig: apiConfig.kitplayDevice,
            options: {
                pageSize: DEFAULT_TABLE_ITEM_SIZE,
                objectName: translate.formatMessage(message.objectName),
            },
            override: (funcs) => {
                funcs.getList = () => {
                    const params = mixinFuncs.prepareGetListParams(queryFilter);
                    mixinFuncs.handleFetchList({ ...params, customerId });
                };
                funcs.mappingData = (response) => {
                    if (response.result === true) {
                        setFetchedAt(new Date());
                        return {
                            data: response.data.content,
                            total: response.data.totalElements,
                        };
                    }
                };
                funcs.getItemDetailLink = (dataRow) => {
                    const currentPath = location.pathname;
                    const searchParams = location.search;
                    return `${currentPath}/${dataRow.id}${searchParams}`;
                };

                funcs.getCreateLink = () => {
                    const currentPath = location.pathname;
                    const searchParams = location.search;
                    return `${currentPath}/create${searchParams}`;
                };
                funcs.additionalActionColumnButtons = () => {
                    if (!mixinFuncs.hasPermission([apiConfig.kitplayDevice.approveDevice.baseURL])) return {};
                    return {
                        loginStatus: (record) => {
                            return <LoginStatusIndicator lastTimeLogin={record.lastTimeLogin} fetchedAt={fetchedAt} translate={translate} />;
                        },
                    };
                };
                funcs.changeFilter = (filter) => {
                    const customerName = queryParams.get('customerName');
                    const params = {
                        customerName,
                        customerId,
                        ...filter,
                    };
                    const filteredParams = Object.fromEntries(
                        Object.entries(params).filter(([_, value]) => value != null),
                    );
                    mixinFuncs.setQueryParams(serializeParams(filteredParams));
                };
            },
        });

    const columns = [
        {
            title: <FormattedMessage defaultMessage="Device name" />,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: <FormattedMessage defaultMessage="Device ID" />,
            dataIndex: 'deviceId',
            align: 'left',
            width: 180,
        },
        {
            title: <FormattedMessage defaultMessage="Expiration date" />,
            width: 180,
            dataIndex: 'expireDate',
            align: 'right',
            render: (createdDate) => {
                const createdDateLocal = convertUtcToLocalTime(createdDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                return <div>{createdDateLocal}</div>;
            },
        },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                edit: mixinFuncs.hasPermission([apiConfig.device.update.baseURL]),
                delete: mixinFuncs.hasPermission([apiConfig.device.delete.baseURL]),
                loginStatus: true,
            },
            {
                width: '160px',
                className: styles['action-column'],
                onClick: (e) => {
                    e.stopPropagation();
                },
            },
        ),
    ];

    const searchFields = [
        {
            key: 'name',
            placeholder: translate.formatMessage(commonMessage.deviceName),
            colSpan: 4,
        },
        {
            key: 'deviceId',
            placeholder: translate.formatMessage(commonMessage.deviceId),
            colSpan: 4,
        },
        {
            key: 'status',
            placeholder: translate.formatMessage(commonMessage.status),
            type: FieldTypes.SELECT,
            options: statusValues,
            colSpan: 4,
        },
        {
            key: 'expireStatus',
            placeholder: translate.formatMessage(commonMessage.expireDate),
            type: FieldTypes.SELECT,
            options: expireValues,
            colSpan: 6,
        },
    ];

    return (
        <PageWrapper
            routes={[
                { breadcrumbName: translate.formatMessage(commonMessage.kitPlayShop), path: routes.shopAccountListPage.path },
                { breadcrumbName: translate.formatMessage(commonMessage.piService) },
            ]}
        >
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({
                    fields: searchFields,
                    initialValues: queryFilter,
                })}
                actionBar={mixinFuncs.renderActionBar()}
                baseTable={
                    <BaseTable
                        onChange={changePagination}
                        pagination={pagination}
                        loading={loading}
                        dataSource={data}
                        columns={columns}
                    // onRow={(record, rowIndex) => ({
                    //     onClick: (e) => {
                    //         e.stopPropagation();
                    //         setData(routes.DeviceTab.keyActiveTab, 'device');
                    //         navigate(
                    //             `${location.pathname}/group-device?deviceId=${record.id}&deviceName=${
                    //                 record.name
                    //             }`,
                    //         );
                    //     },
                    // })}
                    />
                }
            />
        </PageWrapper>
    );
};

export default DeviceListPage;
